import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { toast } from "react-toastify";
import { FaEnvelope, FaArrowLeft, FaSpinner, FaKey } from 'react-icons/fa';
import yogBg from '../images/yog-bg.jpg';
import HttpInterceptor from '../lib/HttpInterceptor';
import LoginHeader from '../Components/LoginHeader';
import Footer from '../Components/Footer';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [identifierValue, setIdentifierValue] = useState('');
  const [identifierType, setIdentifierType] = useState('email'); // 'email' or 'mobile'
  const [otpSent, setOtpSent] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const validationSchema = Yup.object().shape({
    identifier: Yup.string()
      .required('Email or Mobile number is required')
      .test('is-email-or-mobile', 'Invalid email or mobile number', (value) => {
        if (!value) return false;
        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        const isMobile = /^[6-9]\d{9}$/.test(value.replace(/\D/g, ''));
        return isEmail || isMobile;
      })
  });

  const otpValidationSchema = Yup.object({
    otp: Yup.string()
      .matches(/^\d{6}$/, 'OTP must be exactly 6 digits')
      .required('OTP is required')
  });

  const handleIdentifierSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      const val = values.identifier.trim();
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
      const type = isEmail ? 'email' : 'mobile';
      const cleanVal = isEmail ? val : val.replace(/\D/g, '');

      const payload = { [type]: cleanVal };
      await HttpInterceptor.post('/auth/forgot-password', payload);
      
      setIdentifierValue(cleanVal);
      setIdentifierType(type);
      setOtpSent(true);
      setResendTimer(60);
      toast.success(`OTP has been sent to your ${type === 'email' ? 'email' : 'mobile via WhatsApp'}.`);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to send OTP. Try again.';
      setErrors({ submit: msg });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Timer countdown effect
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    
    setIsResending(true);
    try {
      const payload = { [identifierType]: identifierValue };
      await HttpInterceptor.post('/auth/forgot-password', payload);
      setResendTimer(60);
      toast.success(`OTP has been resent to your ${identifierType}.`);
    } catch (err) {
      toast.error('Failed to resend OTP. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleOTPSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      await HttpInterceptor.post('/auth/verify-otp', {
        [identifierType]: identifierValue,
        otp: values.otp
      });
      
      navigate(`/reset-password?${identifierType}=${encodeURIComponent(identifierValue)}&otp=${values.otp}`);
    } catch (err) {
      const msg = err.response?.data?.message || 'Invalid OTP. Please try again.';
      setErrors({ submit: msg });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (otpSent) {
    return (
      <>
        <LoginHeader />
        <section className="min-h-screen flex items-center justify-center p-4 relative">
          <div className="absolute inset-0 -z-10">
            <img src={yogBg} alt="Yoga background" className="w-full h-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-r from-teal-900/80 to-emerald-900/80"></div>
          </div>

          <div className="relative z-10 w-full max-w-md my-8">
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-white/20">
              <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-6 text-center">
                <h2 className="text-2xl md:text-3xl font-bold text-white">Enter OTP</h2>
                <p className="text-white/80 mt-1">Check your {identifierType === 'email' ? 'email' : 'WhatsApp'} for the 6-digit code</p>
              </div>

              <Formik
                initialValues={{ otp: '' }}
                validationSchema={otpValidationSchema}
                onSubmit={handleOTPSubmit}
              >
                {({ isSubmitting }) => (
                  <Form className="p-6 space-y-6">
                    <ErrorMessage name="submit" component="div" className="text-red-600 text-sm text-center" />

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                      {identifierType === 'email' ? <FaEnvelope className="text-3xl text-blue-600 mx-auto mb-2" /> : <div className="text-3xl mb-2">📱</div>}
                      <p className="text-sm text-gray-700 mb-1">
                        We've sent a 6-digit OTP to
                      </p>
                      <p className="text-sm font-semibold text-gray-900">{identifierValue}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        The OTP will expire in 10 minutes
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="otp" className="text-sm font-medium text-gray-700">
                        6-Digit OTP
                        <span className="text-red-500 ml-1" aria-label="required">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                          <FaKey className="h-5 w-5 text-gray-400" />
                        </div>
                        <Field
                          name="otp"
                          id="otp"
                          type="text"
                          placeholder="••••••"
                          maxLength="6"
                          className="pl-10 w-full border rounded-lg py-2 px-3 text-center text-2xl tracking-widest focus:ring-teal-500 placeholder:text-2xl placeholder:tracking-widest placeholder:text-gray-400"
                          aria-required="true"
                          aria-describedby="otp-error"
                          autoComplete="one-time-code"
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(/\D/g, '');
                          }}
                        />
                      </div>
                      <ErrorMessage name="otp" component="p" id="otp-error" className="text-red-500 text-xs" role="alert" aria-live="polite" />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg ${
                        isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:from-teal-700 hover:to-emerald-700'
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          <span>Verifying...</span>
                        </>
                      ) : (
                        <span>Verify OTP</span>
                      )}
                    </button>

                    <div className="text-center pt-4 border-t border-gray-200 space-y-2">
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        disabled={isResending || resendTimer > 0}
                        className={`text-sm font-medium transition-all ${
                          resendTimer > 0 
                            ? 'text-gray-700 bg-gray-100 px-4 py-2 rounded-lg cursor-not-allowed' 
                            : 'text-teal-600 hover:text-teal-500 hover:underline'
                        } disabled:opacity-50`}
                      >
                        {isResending 
                          ? 'Resending...' 
                          : resendTimer > 0 
                            ? `Resend OTP in ${resendTimer}s`
                            : "Didn't receive OTP? Resend"
                        }
                      </button>
                      <div>
                        <Link to="/login" className="text-sm text-teal-600 hover:text-teal-500 flex items-center justify-center">
                          <FaArrowLeft className="mr-2" />
                          Back to Login
                        </Link>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <LoginHeader />
      <section className="min-h-screen flex items-center justify-center p-4 relative">
        <div className="absolute inset-0 -z-10">
          <img src={yogBg} alt="Yoga background" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/80 to-emerald-900/80"></div>
        </div>

        <div className="relative z-10 w-full max-w-md my-8">
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-white/20">
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-6 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white">Reset Password</h2>
              <p className="text-white/80 mt-1">Enter your registered email or mobile number</p>
            </div>

            <Formik
              initialValues={{ identifier: '' }}
              validationSchema={validationSchema}
              onSubmit={handleIdentifierSubmit}
            >
              {({ isSubmitting }) => (
                <Form className="p-6 space-y-6">
                  <ErrorMessage name="submit" component="div" className="text-red-600 text-sm text-center" />

                  <div className="space-y-2">
                    <label htmlFor="identifier" className="text-sm font-medium text-gray-700">
                      Email or Mobile Number
                      <span className="text-red-500 ml-1" aria-label="required">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                        <FaEnvelope className="h-5 w-5 text-gray-400" />
                      </div>
                      <Field
                        name="identifier"
                        id="identifier"
                        type="text"
                        placeholder="your@email.com or 10-digit mobile"
                        className="pl-10 w-full border rounded-lg py-2 px-3 focus:ring-teal-500"
                        aria-required="true"
                        aria-describedby="identifier-error"
                      />
                    </div>
                    <ErrorMessage name="identifier" component="p" id="identifier-error" className="text-red-500 text-xs" role="alert" aria-live="polite" />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg ${
                      isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:from-teal-700 hover:to-emerald-700'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        <span>Sending OTP...</span>
                      </>
                    ) : (
                      <span>Send OTP</span>
                    )}
                  </button>

                  <div className="text-center pt-4 border-t border-gray-200">
                    <Link to="/login" className="text-sm text-teal-600 hover:text-teal-500 flex items-center justify-center">
                      <FaArrowLeft className="mr-2" />
                      Back to Login
                    </Link>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default ForgotPassword;
